import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { AtlasViewContext } from '../../organisms/AtlasComponent/AtlasViewContext';
import { useToolbarState } from '../../../context/ToolbarStateContext';
import { getMoleculeVisibility } from '../../../utils/responsiveColumns';
import { UnifiedFilters } from 'types/filters';
import { SortOrder } from 'types/context';
import { useSelector } from 'react-redux';
import { selectSphereId } from '@center-pwa/selectors/selectors';
import './atlas-header.scss';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import StatusIcon from '../status/status';
import UrgencyMenu from '../UrgencyMenu/UrgencyMenu';
import SphereMenu from '../SphereMenu/SphereMenu';

import '../../atoms/urgency/urgency.scss';
import { Badge } from 'primereact/badge';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';

import type { FilterType } from './atlasHeaderUtils';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  aspectKindFromSchemaProperty,
  aspectKindToFilterType,
} from '../../../utils/aspectKind';
import LevelFilterPopoverOrganism from '../../organisms/LevelFilterPopover/LevelFilterPopoverOrganism';
import { VIEW_WIDE_SCOPE, type FilterSaveTarget, type FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import { buildFilterPropertyOptions, buildSchemaPerspectiveOptions, type SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import type { PropertyOption } from '../../molecules/PropertyFilter';
import type { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { ComposedLevelFilters } from 'types/contextView';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';

export interface SphereTypeOption {
  name: string;
  avatar: string;
  type: string;
}

/* ---------------------------------------------------------------------------
   AtlasHeader Component
   Dynamically renders header cells based on view_config.nodeMolecules.
   Supports dynamic sorting and inline filtering for each column.
--------------------------------------------------------------------------- */
export interface HeaderItem {
  label: any;
  className: string;
  // Optional: perspective-selector headers are keyed by `perspectiveSystemName`
  // and carry no molecule type.
  moleculeType?: string;
  perspectiveSystemName?: string;
  moleculeProps?: any;
  // Index of the originating entry in `viewConfig.nodeMolecules`. Absent on
  // synthetic headers (e.g. the spliced-in Status column) that have no backing
  // molecule to select. Used by `onColumnSelect` / `selectedColumnIndex` below.
  nodeMoleculeIndex?: number;
}

export interface FilterSortState {
  filters?: UnifiedFilters;
  sortOrder?: SortOrder[];
}

export interface AtlasHeaderProps {
  baseSchema: any;
  filterSortState: FilterSortState;
  addOrUpdatePropertyFilter: (filter: any) => void;
  addOrUpdatePerspectiveFilter: (filter: any) => void;
  removePropertyFilterByKey: (property: string) => void;
  addOrUpdateSortOrder: (sort: any) => void;
  removeSortOrderByKey: (property: string) => void;
  resetFilterSortState: () => void;
  featuredSphereId: string;
  organizationId: string;
  featuredSphereDirection?: 'outwards' | 'inwards' | 'both'; // Direction for featured sphere
  featuredSphereHops?: number; // Number of hops based on expansion level
  onHeaderWidthCalculated?: (width: number) => void;
  onlyNodeLabel?: boolean;
  hideTemplateLabel?: boolean;
  className?: string;
  onIndividualWidthsCalculated?: (
    widths: {
      label: any;
      width: number;
      className: string;
      perspectiveSystemName: string;
      nodeMoleculeIndex?: number;
    }[]
  ) => void;
  /**
   * Called with a header's `nodeMoleculeIndex` when its cell is clicked.
   * Selection-only — mutates nothing. Set only by the Atlas view editor's live
   * preview (`AtlasLivePreviewMolecule`) so "the preview IS the column picker"
   * (spec Phase 7 / OQ-B); undefined on every real board render.
   */
  onColumnSelect?: (nodeMoleculeIndex: number) => void;
  /** The currently selected column's `nodeMoleculeIndex`, for header tinting. */
  selectedColumnIndex?: number | null;
  /**
   * Called with the columns the current responsive breakpoint dropped (survivors are
   * excluded). Drives the live preview's "+N hidden" badge, which names — not just
   * counts — what was dropped (spec Phase 4 / dev note 103).
   */
  onHiddenColumnsCalculated?: (
    hidden: { nodeMoleculeIndex: number; label: string }[]
  ) => void;
  /**
   * The view-wide ('*') composed filter stack (`useLevelFilters`, EAINT-931) —
   * an Aspect column's `LevelFilterPopoverOrganism` narrows it to its own
   * property via `focusProperty`. Defaulted so the `onlyNodeLabel` mount
   * (which renders no Aspect columns) doesn't need to supply it.
   */
  composedColumnFilters?: ComposedLevelFilters;
  columnFilterSaveTargets?: FilterSaveTargetAvailability[];
  onColumnFilterSave?: (filters: UnifiedFilters, target: FilterSaveTarget) => void;
  activeViewDisplayName?: string;
  /** Arc templates in view — the Stage column's `LevelFilterPopoverOrganism`
   * (`focusArc="primary"`) needs these to populate its template/stage picker. */
  arcTemplates?: ArcTemplateOption[];
  /** Schemas in scope — forwarded to the Stage column's popover so its catalog
   * can name the types the arc filter applies to. */
  allSchemas?: SchemaForFilters[];
}

/**
 * Fallback column label for an aspect the base schema doesn't declare:
 * splits camelCase, normalises `snake_case`/`kebab-case` separators, and
 * capitalises. Used only when no schema label is resolvable.
 */
/**
 * Perspectives every schema effectively has, even one whose own `suppressPerspectives`
 * drops it from `perspectives[]` for rows of its own type (`computeHeaderItem`'s
 * `PerspectiveSelector` case) — mirrors the three `buildSchemaPerspectiveOptions`
 * (filterPropertyBuilder.ts) already treats as always-available defaults.
 */
const UNIVERSAL_PERSPECTIVE_SYSTEM_NAMES = new Set([
  'center_parent',
  'center_responsible',
  'center_center',
]);

function humaniseAspectKey(key: string): string {
  return key
    .replaceAll(/([A-Z])/g, ' $1')
    .replaceAll(/[_-]+/g, ' ')
    .replace(/^./, (str: string) => str.toUpperCase())
    .trim();
}

const AtlasHeader: React.FC<AtlasHeaderProps> = ({
  baseSchema,
  filterSortState,
  addOrUpdatePropertyFilter,
  addOrUpdatePerspectiveFilter,
  removePropertyFilterByKey,
  addOrUpdateSortOrder,
  removeSortOrderByKey,
  resetFilterSortState,
  featuredSphereId,
  organizationId,
  featuredSphereDirection = 'outwards', // Default to outwards
  featuredSphereHops = 1, // Default to 1 hop
  onHeaderWidthCalculated,
  onlyNodeLabel,
  hideTemplateLabel = false,
  className,
  onIndividualWidthsCalculated,
  onColumnSelect,
  selectedColumnIndex = null,
  onHiddenColumnsCalculated,
  composedColumnFilters = { runtime: {}, display: {}, provenance: [] },
  columnFilterSaveTargets = [],
  onColumnFilterSave = () => {
    /* no-op — AtlasTree always supplies the real save handler */
  },
  activeViewDisplayName,
  arcTemplates = [],
  allSchemas = [],
}) => {
  const { t } = useTranslation('schema');
  const logAnalyticsEvent = useAnalytics();
  const context = useContext(AtlasViewContext);
  const apiClients = useApiClientsOrNull();
  // Fetch available sphere types for the filter/select UI — called before the
  // apiClients guard so the hook runs unconditionally (hooks must not be conditional).
  const { schemas: availableSphereTypeSchemas, loading: availableTypesLoading } =
    useAvailableTypes(apiClients, featuredSphereId);
  const { responsiveBreakpoint } = useToolbarState();

  if (!apiClients) {
    return null;
  }

  const { schemaClient, graphClient } = apiClients;

  const urgencyMenuRef = useRef<Menu>(null);

  const urgencyOptions: UrgencyOption[] = [
    { name: 'Urgent', code: '5', avatar: 'urgent-icon' },
    { name: 'High', code: '4', avatar: 'high-icon' },
    { name: 'Medium', code: '3', avatar: 'medium-icon' },
    { name: 'Low', code: '2', avatar: 'low-icon' },
    { name: 'Parked', code: '1', avatar: 'parked-icon' },
  ];
  const { viewConfig } = context ?? {};
  const nodeMolecules = viewConfig?.nodeMolecules ?? [];

  const sphereID = useSelector(selectSphereId); // Get the sphere ID from the Redux store
  const [loadingSphereTypes, setLoadingSphereTypes] = useState(false);

  const handleUrgencySelection = (
    option: UrgencyOption,
    e: React.MouseEvent | React.KeyboardEvent
  ) => {
    e.stopPropagation();

    setUrgencySelectedOptions((prevSelected) => {
      const updated = prevSelected.some((o) => o.code === option.code)
        ? prevSelected.filter((o) => o.code !== option.code)
        : [...prevSelected, option];

      const urgencyCodes = updated.map((o) => o.code);
      if (urgencyCodes.length > 0) {
        addOrUpdatePropertyFilter({
          property: 'urgency',
          operator: 'in',
          values: urgencyCodes,
        });
      } else {
        removePropertyFilterByKey('urgency');
      }

      return updated;
    });

    // Keep the menu open after selection
    setTimeout(() => {
      urgencyMenuRef.current?.show(e as React.MouseEvent);
    });
  };

  // Sorting integration: toggle sort order for a given property.
  const toggleSort = (property: string) => {
    const currentSortOrders = filterSortState.sortOrder || [];
    const currentSort = currentSortOrders.find(
      (s: any) => s.property === property
    );
    if (!currentSort) {
      addOrUpdateSortOrder({ property, orderBy: 'asc' });
    } else if (currentSort.orderBy === 'asc') {
      addOrUpdateSortOrder({ property, orderBy: 'desc' });
    } else if (currentSort.orderBy === 'desc') {
      removeSortOrderByKey(property);
    }
  };

  const getSortIcon = (property: string) => {
    const currentSort = (filterSortState.sortOrder || []).find(
      (s: any) => s.property === property
    );
    if (!currentSort) return '⇅';
    return currentSort.orderBy === 'asc' ? '▲' : '▼';
  };
  // Now we add a new field "perspectiveSystemName".

  // Builds a header cell for one `nodeMolecules` entry, independent of responsive
  // visibility — extracted so the visibility gate (below) can tell "not a column"
  // apart from "a column that's currently hidden," which it needs to report the
  // dropped-column labels for the "+N hidden" badge.
  const computeHeaderItem = (
    molecule: any,
    nodeMoleculeIndex: number
  ): HeaderItem | null => {
    switch (molecule.type) {
        case 'NodePerspectivePill':
          return null; // not rendered as header
        case 'NodeCounts':
          return null;
        case 'NodeLabel':
          return {
            label: t('atlas.column.node'),
            className: onlyNodeLabel ? 'col-12' : 'col-6',
            moleculeType: molecule.type,
            moleculeProps: molecule.moleculeProps,
            nodeMoleculeIndex,
          };
        case 'NodeDetailsButton':
          return {
            label: 'Description',
            className: 'col-2',
            moleculeType: molecule.type,
            moleculeProps: molecule.moleculeProps,
            nodeMoleculeIndex,
          };
        case 'ApproachBadges':
          // Node.tsx renders this as a real column (deployed-approach chips), so it
          // needs a header counterpart like every other configured column — this case
          // was previously missing, which desynced the header/data-row cell counts.
          // The column is icon-only (narrow col-1); no text label is shown in the header,
          // matching the Status column's decorative-header pattern.
          return {
            label: '',
            className: 'col-1 node-approach-badges',
            moleculeType: molecule.type,
            moleculeProps: molecule.moleculeProps,
            nodeMoleculeIndex,
          };
        case 'PerspectiveSelector': {
          const perspectiveSystemName = molecule.moleculeProps.perspectiveSystemName;
          let perspective = baseSchema?.perspectives?.find(
            (p: any) => p.system_name === perspectiveSystemName
          );
          // A root type can suppress a universal perspective for ITSELF (e.g. an
          // organization has no Purpose/parent of its own) while a view still shows
          // it as a column for the differently-typed children underneath — baseSchema
          // is always the ROOT sphere's type (see AtlasTree's comment on baseSchemaType),
          // so `suppressPerspectives` there must never silently drop a column header
          // that real rows below still render real values for. Falls back to the
          // universal `center_baseschema` definition these three are always declared
          // on, unsuppressed — the same always-available set `buildSchemaPerspectiveOptions`
          // already hardcodes for the filter picker.
          if (!perspective && UNIVERSAL_PERSPECTIVE_SYSTEM_NAMES.has(perspectiveSystemName)) {
            perspective = {
              label_key: `schema.center_baseschema.perspective.${perspectiveSystemName}.label`,
              name: humaniseAspectKey(perspectiveSystemName),
            };
          }
          if (!perspective) {
            return null;
          }
          const perspectiveLabel = t(
            // strip off the "schema." prefix
            perspective.label_key.replace(/^schema\./, ''),
            { defaultValue: perspective.name }
          );
          return {
                label: perspectiveLabel,
                className: 'col-1 node-perspective-selector',
                perspectiveSystemName:
                  molecule.moleculeProps.perspectiveSystemName,
                nodeMoleculeIndex,
          };
        }
        case 'ArcStageSelector':
        case 'FlowStageSelector': {
          return {
            label: t('atlas.column.stage'),
            className: 'col-1 node-arc-stage',
            moleculeType: molecule.type,
            moleculeProps: molecule.moleculeProps,
            nodeMoleculeIndex,
          };
        }
        case 'Aspect': {
          const propertyKey = molecule.moleculeProps?.aspect;
          if (propertyKey) {
            // Prefer the schema's own label so the board agrees with the aspect
            // picker; snake_case keys like `due_date` humanise to "Due_date"
            // otherwise. Falls back to the humanised key for aspects the base
            // schema doesn't declare (mixed-type levels).
            const propertyDef = baseSchema?.properties?.find(
              (prop: any) => prop.name === propertyKey
            );
            const label = propertyDef?.label_key
              ? t(propertyDef.label_key.replace(/^schema\./, ''), {
                  defaultValue: propertyDef.label || humaniseAspectKey(propertyKey),
                })
              : propertyDef?.label || humaniseAspectKey(propertyKey);
            return {
              label,
              className: 'col-1 node-sphere-property',
              moleculeType: molecule.type,
              moleculeProps: molecule.moleculeProps,
              nodeMoleculeIndex,
            };
          }
          return null;
        }
        default:
          return null;
      }
  };

  const hiddenColumns: { nodeMoleculeIndex: number; label: string }[] = [];

  const headers: HeaderItem[] = nodeMolecules
    .map((molecule: any, nodeMoleculeIndex: number) => {
      const headerItem = computeHeaderItem(molecule, nodeMoleculeIndex);
      if (!headerItem) {
        return null;
      }

      // Use responsive visibility system instead of simple toolbar expansion check
      const visibilityConfig = getMoleculeVisibility(
        molecule.type,
        molecule.moleculeProps?.important === true,
        responsiveBreakpoint,
        molecule.moleculeProps
      );

      // Skip rendering if molecule should not be visible at this breakpoint — but
      // record it, so the preview's "+N hidden" badge can name what it dropped.
      if (!visibilityConfig.isVisible) {
        hiddenColumns.push({
          nodeMoleculeIndex,
          label:
            typeof headerItem.label === 'string'
              ? headerItem.label
              : t(`context.viewEditor.atlas.molecule.${molecule.type}`, {
                  defaultValue: molecule.type,
                }),
        });
        return null;
      }

      return headerItem;
    })
    .filter(
      (header): header is NonNullable<typeof header> => header !== null
    );

  // If onlyNodeLabel is true, filter out all except NodeLabel
  const filteredHeaders = onlyNodeLabel
    ? headers.filter((header) => header.moleculeType === 'NodeLabel')
    : headers;

  const stableFilteredHeaders = useMemo(
    () => filteredHeaders,
    [
      filteredHeaders
        .map((header) => `${header.moleculeType}-${header.className}`)
        .join('|'),
    ]
  );

  const hiddenColumnsKey = hiddenColumns
    .map((h) => `${h.nodeMoleculeIndex}-${h.label}`)
    .join('|');
  useEffect(() => {
    onHiddenColumnsCalculated?.(hiddenColumns);
    // `hiddenColumnsKey` is the actual dependency; `hiddenColumns` itself is a new
    // array reference every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenColumnsKey, onHiddenColumnsCalculated]);

  useEffect(() => {
    const widths = headerRefs.current.map((el) => el?.offsetWidth ?? 0);

    if (!widths.every((w, i) => w === previousWidthsRef.current[i])) {
      previousWidthsRef.current = widths;

      const enrichedWidths = stableFilteredHeaders.map((header, i) => ({
        label: header.label,
        width: widths[i] ?? 0,
        className: header.className,
        perspectiveSystemName: header.perspectiveSystemName || '',
        nodeMoleculeIndex: header.nodeMoleculeIndex,
      }));

      setHeaderWidths(enrichedWidths);
    }
  }, [stableFilteredHeaders]);

  const nodeLabelIndex = headers.findIndex(
    (h) => h.moleculeType === 'NodeLabel'
  );
  if (nodeLabelIndex !== -1) {
    // A fixed, non-column cell: it exists only so `Node.tsx` can measure a width for
    // the real status badge it renders separately (`columnsStyle.status`, keyed off
    // this cell's `status-header` className) — it has no `nodeMoleculeIndex` because
    // it does not correspond to a `nodeMolecules` entry. It was previously an inert
    // menu trigger: the click/key handlers that would have opened it were commented
    // out, and the `Menu` it never opened held hard-coded, non-localized status names.
    // The real status interaction lives on the status badge itself (`handleStatusClick`
    // in `Node.tsx`), so this cell is decorative — a plain, accessible icon, not a
    // dead button.
    const statusHeader: HeaderItem = {
      label: (
        <span
          className="atlas-header__status-icon"
          role="img"
          aria-label={t('context.viewEditor.atlas.statusHeaderLabel')}
        >
          <StatusIcon color="currentColor" />
        </span>
      ),
      className: 'status-header',
      moleculeType: 'Status',
    };

    headers.splice(nodeLabelIndex + 1, 0, statusHeader);
  }
  if (headers.length > 0) {
    const lastHeader = headers.at(-1)!;
    if (lastHeader.className === 'col-1 node-perspective-selector') {
      lastHeader.className = 'col node-perspective-selector';
    } else if (lastHeader.className === 'col-1 node-arc-stage') {
      // Keep col-1 node-arc-stage as is to maintain fixed width
    } else if (lastHeader.className === 'status-header') {
      lastHeader.className = 'status-header';
      if (headers.length > 1) {
        const previousHeader = headers.at(-2)!;
        previousHeader.className = 'col';
      }
    } else {
      lastHeader.className = 'col-1';
    }
  }
  const mapHeaderToProperty = (headerLabel: any): string => {
    if (!headerLabel || typeof headerLabel !== 'string') {
      return '';
    }

    const propertyMap: Record<string, string> = {
      'Start Date': 'startDate',
      'Target Date': 'targetDate',
      Description: 'description',
      [t('atlas.column.node')]: 'name',
    };

    const property =
      propertyMap[headerLabel] || headerLabel.toLowerCase().replace(/\s+/g, '');
    return property;
  };

  /**
   * The property key a column's filter is written against, derived from the
   * column's KIND — never its (sometimes translated) label, which
   * `mapHeaderToProperty` guesses at and which breaks under i18n/relabeling.
   * `Aspect` carries the schema system name directly; `NodeLabel` and
   * `NodeDetailsButton` are the platform's canonical identity properties
   * (`filterPropertyBuilder.ts`). Every other kind (`ArcStageSelector`,
   * `FlowStageSelector`, `ApproachBadges`, `Status`, …) has no real scalar
   * property to filter on, so it has no key — callers must not render a
   * filter trigger for a column with no key.
   */
  const getFilterPropertyKey = (header: HeaderItem): string | undefined => {
    switch (header.moleculeType) {
      case 'Aspect':
        return header.moleculeProps?.aspect;
      case 'NodeLabel':
        return 'name';
      case 'NodeDetailsButton':
        return 'description';
      default:
        return undefined;
    }
  };

  /**
   * Kind resolution is NOT decided here — it comes from the shared
   * `aspectKindFromSchemaProperty`, the same function the renderer uses. This block
   * previously mirrored that logic "without importing it" because the original took a
   * `rawValue` the header has no access to; the shared version makes that argument
   * optional, so there is nothing left to mirror. All this decides now is what a given
   * kind can be FILTERED as.
   */
  const getAspectFilterType = (aspectKey?: string): FilterType => {
    if (!aspectKey) return 'text';
    const propertyDef = baseSchema?.properties?.find(
      (prop: any) => prop.name === aspectKey
    );
    return aspectKindToFilterType(aspectKindFromSchemaProperty(propertyDef));
  };

  /**
   * `PropertyOption`s for `baseSchema`'s own properties, built through the
   * same type/enum-mapping `buildFilterPropertyOptions` gives every other
   * filter catalog (TreeMap's rail, the advanced editor) — rather than
   * re-deriving `enumValues` from `metadata.options` here a second time.
   * Scoped to one schema (not the org-wide catalog) because a single Aspect
   * column edits exactly one property, the same convention
   * `ColumnFilterHeaderAtom` documents for its own single-entry catalog.
   */
  const aspectPropertyOptions = useMemo(
    () =>
      baseSchema
        ? buildFilterPropertyOptions({
            schemas: [baseSchema] as SchemaForFilters[],
            t,
            includeArcProperties: false,
            includeCustomProperties: true,
          })
        : [],
    [baseSchema, t]
  );

  /**
   * `PerspectiveOption`s for a `PerspectiveSelector` column's
   * `LevelFilterPopoverOrganism` (W5). `header.perspectiveSystemName` is the
   * column's real discriminant — `header.moleculeType` is never set for these
   * headers (see `HeaderItem`).
   */
  const schemaPerspectiveOptions = useMemo<PerspectiveOption[]>(
    () =>
      buildSchemaPerspectiveOptions(baseSchema, t).map((p) => ({
        systemName: p.systemName,
        label: p.displayName,
      })),
    [baseSchema, t]
  );

  /** The single-entry catalog `LevelFilterPopoverOrganism` edits a column's
   * `focusProperty` against. An Aspect column prefers its schema-defined
   * option (label, enum values, operator set); every other kind — and an
   * Aspect whose key isn't in `baseSchema.properties` (mixed-type levels,
   * same fallback `computeHeaderItem`'s label uses) — falls back to a
   * minimal option built from the header itself. */
  const getColumnPropertyOptions = (header: HeaderItem): PropertyOption[] => {
    const propertyKey = getFilterPropertyKey(header);
    if (!propertyKey) return [];
    if (header.moleculeType === 'Aspect') {
      const fromSchema = aspectPropertyOptions.filter((p) => p.key === propertyKey);
      if (fromSchema.length > 0) return fromSchema;
    }
    return [
      {
        key: propertyKey,
        label: typeof header.label === 'string' ? header.label : propertyKey,
        type: getFilterType(header) as PropertyOption['type'],
      },
    ];
  };

  /**
   * Wraps `onColumnFilterSave` for a column's `LevelFilterPopoverOrganism`
   * popover: fires `atlas_column_filter_applied` with the real property key
   * (an Aspect's schema system name, never the humanised label) before
   * delegating to the save the host actually wires up. Every column kind
   * (Aspect, NodeLabel, generic) now shares this save path; urgency/sphere-type
   * are separate selectors, not `LevelFilterPopoverOrganism` instances.
   */
  /** `ArcStageSelector`/`FlowStageSelector` columns have no scalar property —
   * they filter the sphere's primary arc, not `getFilterPropertyKey`'s domain. */
  const isArcStageColumn = (header: HeaderItem): boolean =>
    header.moleculeType === 'ArcStageSelector' || header.moleculeType === 'FlowStageSelector';

  const handleColumnFilterSave =
    (header: HeaderItem) => (filters: UnifiedFilters, target: FilterSaveTarget) => {
      logAnalyticsEvent('atlas_column_filter_applied', undefined, {
        contextSystemName: context?.contextSystemName ?? null,
        property:
          getFilterPropertyKey(header) ??
          header.perspectiveSystemName ??
          (isArcStageColumn(header) ? 'arc:primary' : undefined),
        target,
      });
      onColumnFilterSave(filters, target);
    };

  /**
   * A column's filter trigger, or nothing when the column has no real property
   * to filter on. `Aspect` needs no branch of its own: `getFilterPropertyKey`
   * already resolves it to `moleculeProps.aspect`. The Stage column has no
   * scalar property at all — it drills into the sphere's primary arc via
   * `focusArc="primary"` instead of `focusProperty`.
   */
  const renderColumnFilterTrigger = (header: HeaderItem) => {
    if (isArcStageColumn(header)) {
      return (
        <LevelFilterPopoverOrganism
          scopeId={VIEW_WIDE_SCOPE}
          composed={composedColumnFilters}
          focusArc="primary"
          arcTemplates={context?.arcTemplates ?? arcTemplates}
          allSchemas={context?.allSchemas ?? allSchemas}
          contextTierRows={context?.globalContextTiers}
          onOpen={() => context?.onColumnFilterOpen?.('arc:primary')}
          onClose={() => context?.onColumnFilterOpen?.(undefined)}
          canEditContext={false}
          saveTargets={columnFilterSaveTargets}
          onSave={handleColumnFilterSave(header)}
          onShareWithView={context?.onShareWithView}
          onViewPermissionDenied={context?.onViewPermissionDenied}
          availableProperties={[]}
          availablePerspectives={[]}
          triggerLabel={t('atlas.column.stage')}
          activeViewDisplayName={activeViewDisplayName}
        />
      );
    }
    const propertyKey = getFilterPropertyKey(header);
    if (!propertyKey) return null;
    return (
      <LevelFilterPopoverOrganism
        scopeId={VIEW_WIDE_SCOPE}
        composed={composedColumnFilters}
        focusProperty={propertyKey}
        contextTierRows={context?.globalContextTiers}
        onOpen={() => context?.onColumnFilterOpen?.(propertyKey)}
        onClose={() => context?.onColumnFilterOpen?.(undefined)}
        canEditContext={false}
        saveTargets={columnFilterSaveTargets}
        onSave={handleColumnFilterSave(header)}
        onShareWithView={context?.onShareWithView}
        onViewPermissionDenied={context?.onViewPermissionDenied}
        availableProperties={getColumnPropertyOptions(header)}
        availablePerspectives={[]}
        allSchemas={context?.allSchemas ?? allSchemas}
        perspectiveSearchProps={context?.perspectiveSearchProps}
        triggerLabel={typeof header.label === 'string' ? header.label : propertyKey}
        activeViewDisplayName={activeViewDisplayName}
      />
    );
  };

  const getFilterType = (header: HeaderItem): FilterType => {
    switch (header.moleculeType) {
      case 'Aspect':
        return getAspectFilterType(header.moleculeProps?.aspect);
      case 'NodeLabel':
      case 'NodeDetailsButton':
        return 'text';
      default:
        return 'text';
    }
  };

  const isSortable = (moleculeType?: string): boolean => {
    return moleculeType
      ? [
          'NodeLabel',
          'NodeDetailsButton',
          'Aspect',
          'PerspectiveSelector',
        ].includes(moleculeType)
      : false;
  };
  const urgencyRef = useRef<HTMLDivElement>(null);
  const sphereRef = useRef<HTMLDivElement>(null);
  const sphereMenuRef = useRef<Menu>(null);
  const headerRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [headerWidths, setHeaderWidths] = useState<
    {
      label: any;
      width: number;
      className: string;
      perspectiveSystemName: string;
      nodeMoleculeIndex?: number;
    }[]
  >([]);
  const previousWidthsRef = useRef<number[]>([]);
  useEffect(() => {
    if (stableFilteredHeaders.length === headerWidths.length) {
      const headerLabelWidthMap = stableFilteredHeaders.map(
        (header, index) => ({
          label: header.label,
          width: headerWidths[index] ?? 0,
          className: header.className,
          perspectiveSystemName: header.perspectiveSystemName,
          nodeMoleculeIndex: header.nodeMoleculeIndex,
        })
      );

      // Log or use the map

      // You can pass it elsewhere, update context, or state:
    }
  }, [stableFilteredHeaders, headerWidths]);

  useLayoutEffect(() => {
    const widths = headerRefs.current.map((el) => el?.offsetWidth ?? 0);
    if (!widths.every((w, i) => w === (headerWidths[i]?.width ?? 0))) {
      const newHeaderWidths = stableFilteredHeaders.map((header, index) => ({
        label: header.label,
        width: widths[index],
        className: header.className,
        perspectiveSystemName: header.perspectiveSystemName ?? '',
        nodeMoleculeIndex: header.nodeMoleculeIndex,
      }));
      setHeaderWidths(newHeaderWidths);
    }
  }, [stableFilteredHeaders]);
  const [sphereSearch, setSphereSearch] = useState<string>('');
  // Define the UrgencyOption type if not already defined
  type UrgencyOption = {
    name: string;
    code: string;
    avatar: string;
  };

  const [urgencySelectedOptions, setUrgencySelectedOptions] = useState<
    UrgencyOption[]
  >([]);
  const urgencyMenuModel: MenuItem[] = UrgencyMenu({
    options: urgencyOptions,
    selectedOptions: urgencySelectedOptions,
    onSelect: handleUrgencySelection,
  });

  const [showSphereSelect, setshowSphereSelect] = useState(false);
  const [sphereSelectedOptions, setSphereSelectedOptions] = useState<
    SphereTypeOption[]
  >([]);
  useEffect(() => {
    if (onHeaderWidthCalculated && headerWidths.length > 0) {
      const totalWidth = headerWidths.reduce(
        (acc, item) => acc + item.width,
        0
      );
      onHeaderWidthCalculated(totalWidth);
    }
  }, [headerWidths, onHeaderWidthCalculated]);

  useEffect(() => {
    if (onIndividualWidthsCalculated) {
      onIndividualWidthsCalculated(headerWidths);
    }
  }, [headerWidths, onIndividualWidthsCalculated]);
  useEffect(() => {
    const handleResize = () => {
      const widths = headerRefs.current.map((el) => el?.offsetWidth ?? 0);
      if (!widths.every((w, i) => w === previousWidthsRef.current[i])) {
        previousWidthsRef.current = widths;

        const updatedWidths = stableFilteredHeaders.map((header, index) => ({
          label: header.label,
          width: widths[index],
          className: header.className,
          perspectiveSystemName: header.perspectiveSystemName || '',
          nodeMoleculeIndex: header.nodeMoleculeIndex,
        }));

        setHeaderWidths(updatedWidths);
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [stableFilteredHeaders]);

  useEffect(() => {
    const headerElements = document.querySelectorAll('.atlas-headers li');
    let totalWidth = 0;

    headerElements.forEach((el) => {
      const width = (el as HTMLElement).offsetWidth;
      totalWidth += width;
    });

    totalWidth += 40;

    if (onHeaderWidthCalculated) {
      onHeaderWidthCalculated(totalWidth);
    }
  }, [headers]); // or any dependency that causes headers to rerender
  // Populate sphere-type filter options from the available-types hook.
  // The hook pre-fetches on mount; this effect formats the result when the
  // sphere-select UI opens or when the hook's data changes.
  useEffect(() => {
    if (!showSphereSelect) return;
    setLoadingSphereTypes(availableTypesLoading);
    if (!availableTypesLoading) {
      const formatted = availableSphereTypeSchemas
        .filter((s: any) => !s.is_abstract)
        .map((s: any) => ({
          name: s.name,
          avatar: s.avatar || 'default-icon',
          type: s.system_name,
        }));
      formatted.sort((a: any, b: any) => {
        const nameA = a.name || a.type || '';
        const nameB = b.name || b.type || '';
        return nameA.localeCompare(nameB);
      });
      setSphereTypeOptions(formatted);
    }
  }, [showSphereSelect, availableSphereTypeSchemas, availableTypesLoading]);

  useEffect(() => {
    const widths = headerRefs.current.map((el) => el?.offsetWidth ?? 0);

    if (!widths.every((w, i) => w === previousWidthsRef.current[i])) {
      previousWidthsRef.current = widths;

      const newHeaderWidths = stableFilteredHeaders.map((header, index) => ({
        label: header.label,
        width: widths[index],
        className: header.className,
        perspectiveSystemName: header.perspectiveSystemName ?? '',
        nodeMoleculeIndex: header.nodeMoleculeIndex,
      }));

      setHeaderWidths(newHeaderWidths);
    }
  }, [stableFilteredHeaders, filteredHeaders, onlyNodeLabel]);

  const [sphereTypeOptions, setSphereTypeOptions] = useState<
    SphereTypeOption[]
  >([]);

  const handleSphereOptionToggle = (
    e: React.MouseEvent | React.KeyboardEvent,
    opt: SphereTypeOption
  ) => {
    e.stopPropagation();
    e.preventDefault();

    setSphereSelectedOptions((prevOptions) => {
      const updatedOptions = prevOptions.some((o) => o.type === opt.type)
        ? prevOptions.filter((o) => o.type !== opt.type)
        : [...prevOptions, opt];

      const types = updatedOptions.map((o) => o.type);
      if (types.length) {
        addOrUpdatePropertyFilter({
          property: 'type',
          operator: 'in',
          values: types,
        });
      } else {
        removePropertyFilterByKey('type');
      }

      return updatedOptions;
    });

    setTimeout(() => sphereMenuRef.current?.show(e), 0);
  };
  const sphereMenuModel: MenuItem[] = SphereMenu({
    sphereSearch,
    setSphereSearch,
    loading: loadingSphereTypes,
    sphereOptions: sphereTypeOptions,
    selectedOptions: sphereSelectedOptions,
    onOptionToggle: handleSphereOptionToggle,
    onClear: (e) => {
      e.stopPropagation();
      setSphereSelectedOptions([]);
      removePropertyFilterByKey('type');
      setSphereSearch('');
      sphereMenuRef.current?.hide(e as any);
    },
  });

  return (
    <div className={`atlas-headers ${className ?? ''}`}>
      <ul className="w-full m-0 flex col-12 pb-0 pt-0">
        {filteredHeaders.map((header: HeaderItem, index: number) => {
          let sortProperty = '';
          if (header.moleculeType === 'PerspectiveSelector') {
            sortProperty = `assigned.${header.perspectiveSystemName}.name`;
          } else if (header.moleculeType === 'Aspect') {
            sortProperty = header.moleculeProps?.aspect;
          } else {
            // Prefer the type-based key (NodeLabel→'name', NodeDetailsButton→'description')
            // over string-matching the translated label, which breaks under i18n changes.
            sortProperty = getFilterPropertyKey(header) ?? mapHeaderToProperty(header.label);
          }
          // Editor live-preview only (spec Phase 7 / OQ-B): `onColumnSelect` is
          // supplied exclusively by `AtlasLivePreviewMolecule`. Its absence on every
          // real board render means this branch never fires there.
          const selectableIndex =
            typeof onColumnSelect === 'function' &&
            header.nodeMoleculeIndex !== undefined
              ? header.nodeMoleculeIndex
              : null;
          const isSelectable = selectableIndex !== null;
          const isSelected =
            isSelectable && selectedColumnIndex === selectableIndex;
          const selectColumn = () => {
            if (selectableIndex !== null) {
              onColumnSelect?.(selectableIndex);
            }
          };
          // The overlay button has no text of its own, so it borrows the column's label.
          const selectColumnLabel = t('atlas.column.select', {
            column: header.label ?? header.moleculeType,
          });

          return (
            <li
              ref={(el) => (headerRefs.current[index] = el)}
              key={`${header.moleculeType}-${index}`}
              // Present on every cell backed by a real `nodeMolecules` entry — absent
              // only on synthetic cells (the fixed Status header) that have no column
              // to align against. `Node.tsx` tags its data-row cells with the same
              // attribute/index, so the two can be verified to align (spec Phase 4).
              data-atlas-column-index={header.nodeMoleculeIndex}
              className={`${header.className}${isSelectable ? ' atlas-header__column--selectable' : ''}${
                isSelected ? ' atlas-header__column--selected' : ''
              }`}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor:
                  isSelectable || isSortable(header.moleculeType)
                    ? 'pointer'
                    : 'default',
              }}
            >
              {/* The cell stays a plain list item (a `<ul>` may only hold list items). Column
                  selection is this one real <button>, stretched over the whole cell so a
                  click anywhere lands on it; the cell's own filter / sort / metaspace
                  controls sit above it (atlas-header.scss). An interactive role on the <li>
                  itself could not wrap those controls, and `role="presentation"` took the
                  cell out of the list. */}
              {isSelectable && (
                <button
                  type="button"
                  className="atlas-header__column-select"
                  onClick={selectColumn}
                  aria-pressed={isSelected}
                >
                  <span className="atlas-header__column-select-label">{selectColumnLabel}</span>
                </button>
              )}
              {header.perspectiveSystemName ? (
                <>
                  <span>{header.label}</span>
                  <LevelFilterPopoverOrganism
                    scopeId={VIEW_WIDE_SCOPE}
                    composed={composedColumnFilters}
                    focusPerspective={header.perspectiveSystemName}
                    contextTierRows={context?.globalContextTiers}
                    onOpen={() => header.perspectiveSystemName && context?.onColumnFilterOpen?.(header.perspectiveSystemName)}
                    onClose={() => context?.onColumnFilterOpen?.(undefined)}
                    canEditContext={false}
                    saveTargets={columnFilterSaveTargets}
                    onSave={handleColumnFilterSave(header)}
                    onShareWithView={context?.onShareWithView}
                    onViewPermissionDenied={context?.onViewPermissionDenied}
                    availableProperties={[]}
                    availablePerspectives={schemaPerspectiveOptions}
                    allSchemas={context?.allSchemas ?? allSchemas}
                    perspectiveSearchProps={context?.perspectiveSearchProps}
                    triggerLabel={
                      schemaPerspectiveOptions.find((p) => p.systemName === header.perspectiveSystemName)?.label ??
                      header.perspectiveSystemName
                    }
                    activeViewDisplayName={activeViewDisplayName}
                  />
                  <button
                    className="sort-icon perspective-sort-icon"
                    tabIndex={0}
                    aria-label={t('atlas.column.sort', { column: String(header.label) })}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSort(`assigned.${header.perspectiveSystemName}.name`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSort(`assigned.${header.perspectiveSystemName}.name`);
                      }
                    }}
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'pointer',
                      color: (filterSortState.sortOrder || []).some(
                        (s: any) => s.property === `assigned.${header.perspectiveSystemName}.name`
                      )
                        ? '#3B82F6'
                        : 'var(--sort-icon)',
                    }}
                  >
                    {getSortIcon(`assigned.${header.perspectiveSystemName}.name`)}
                  </button>
                </>
              ) : (
                <>
                  {onlyNodeLabel ? (
                    // first header, single-column mode
                    <>
                      {!hideTemplateLabel && (
                        <>
                          <span className="template-label">
                            {`Template: `}
                            <span className="ml-1 font-bold">{header.label}</span>
                          </span>

                          <LevelFilterPopoverOrganism
                            scopeId={VIEW_WIDE_SCOPE}
                            composed={composedColumnFilters}
                            focusProperty={getFilterPropertyKey(header)}
                            contextTierRows={context?.globalContextTiers}
                            onOpen={() => {
                              const key = getFilterPropertyKey(header);
                              if (key) context?.onColumnFilterOpen?.(key);
                            }}
                            onClose={() => context?.onColumnFilterOpen?.(undefined)}
                            canEditContext={false}
                            saveTargets={columnFilterSaveTargets}
                            onSave={handleColumnFilterSave(header)}
                            onShareWithView={context?.onShareWithView}
                            onViewPermissionDenied={context?.onViewPermissionDenied}
                            availableProperties={getColumnPropertyOptions(header)}
                            availablePerspectives={[]}
                            allSchemas={context?.allSchemas ?? allSchemas}
                            perspectiveSearchProps={context?.perspectiveSearchProps}
                            triggerLabel={
                              typeof header.label === 'string'
                                ? header.label
                                : getFilterPropertyKey(header) ?? header.moleculeType ?? ''
                            }
                            activeViewDisplayName={activeViewDisplayName}
                          />
                        </>
                      )}
                      {isSortable(header.moleculeType) &&
                        header.moleculeType !== 'NodeLabel' && (
                          <button
                            className="sort-icon"
                            aria-label={t('atlas.column.sort', {
                              column:
                                typeof header.label === 'string'
                                  ? header.label
                                  : t(`context.viewEditor.atlas.molecule.${header.moleculeType}`, {
                                      defaultValue: header.moleculeType,
                                    }),
                            })}
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (sortProperty) {
                                toggleSort(sortProperty);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (sortProperty) {
                                  toggleSort(sortProperty);
                                }
                              }
                            }}
                            style={{
                              marginLeft: '0.5rem',
                              cursor: 'pointer',
                              marginTop: '3px',
                              color: (filterSortState.sortOrder || []).some(
                                (s: any) => s.property === sortProperty
                              )
                                ? '#3B82F6'
                              : 'var(--sort-icon)',
                            }}
                          >
                            {sortProperty ? getSortIcon(sortProperty) : ''}
                          </button>
                        )}
                    </>
                  ) : header.moleculeType === 'NodeLabel' ? (
                    // NodeLabel column: urgency/sphere-type filters + Name label + filter + sort.
                    // Keyed on moleculeType, not render index — ApproachBadges or any other
                    // molecule may appear before NodeLabel in nodeMolecules, making index===0
                    // point to the wrong column.
                    <>
                      <div ref={urgencyRef}>
                        <button
                          type="button"
                          tabIndex={0}
                          className="atlas-urgency-btn pl-0 pb-2 pt-0 pr-0"
                          aria-label={t('atlas.urgency.label')}
                          onClick={(e) => urgencyMenuRef.current?.toggle(e)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              urgencyMenuRef.current?.toggle(e);
                            }
                          }}
                        >
                          <i className="atlas-urgency-icon atlas-urgency p-overlay-badge">
                            {urgencySelectedOptions.length > 0 && (
                              <Badge
                                severity="info"
                                value={urgencySelectedOptions.length || 0}
                              />
                            )}
                          </i>
                        </button>

                        <Menu
                          model={urgencyMenuModel}
                          popup
                          ref={urgencyMenuRef}
                          className="urgency-menu"
                        />
                      </div>
                      <div ref={sphereRef}>
                        {/* icon + badge */}
                        <button
                          type="button"
                          className="atlas-sphere-btn pl-0"
                          aria-label={t('atlas.sphereType.label')}
                          tabIndex={0}
                          onClick={(e) => {
                            setshowSphereSelect(true);
                            sphereMenuRef.current?.show(e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setshowSphereSelect(true);
                              sphereMenuRef.current?.show(e);
                            }
                          }}
                        >
                          <i className="center-organization-icon atlas-sphere p-overlay-badge">
                            {sphereSelectedOptions.length > 0 && (
                              <Badge
                                severity="info"
                                value={sphereSelectedOptions.length}
                              />
                            )}
                          </i>
                        </button>

                        {/* our new multi-select menu */}
                        <Menu
                          model={sphereMenuModel}
                          popup
                          ref={sphereMenuRef}
                          className="multi-select-spheretype-menu"
                          onShow={() => setshowSphereSelect(true)}
                          onHide={() => setshowSphereSelect(false)}
                        />
                      </div>

                      <span className="atlas-sphere-name">{t('atlas.column.name')}</span>

                      <LevelFilterPopoverOrganism
                        scopeId={VIEW_WIDE_SCOPE}
                        composed={composedColumnFilters}
                        focusProperty={getFilterPropertyKey(header)}
                        contextTierRows={context?.globalContextTiers}
                        onOpen={() => {
                          const key = getFilterPropertyKey(header);
                          if (key) context?.onColumnFilterOpen?.(key);
                        }}
                        onClose={() => context?.onColumnFilterOpen?.(undefined)}
                        canEditContext={false}
                        saveTargets={columnFilterSaveTargets}
                        onSave={handleColumnFilterSave(header)}
                        availableProperties={getColumnPropertyOptions(header)}
                        availablePerspectives={[]}
                        triggerLabel={t('atlas.column.name')}
                        activeViewDisplayName={activeViewDisplayName}
                      />
                      {isSortable(header.moleculeType) && (
                        <button
                          className="sort-icon"
                          aria-label={t('atlas.column.sort', {
                            column:
                              typeof header.label === 'string'
                                ? header.label
                                : t(`context.viewEditor.atlas.molecule.${header.moleculeType}`, {
                                    defaultValue: header.moleculeType,
                                  }),
                          })}
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sortProperty) {
                              toggleSort(sortProperty);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (sortProperty) {
                                toggleSort(sortProperty);
                              }
                            }
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            cursor: 'pointer',
                            marginTop: '3px',
                            color: (filterSortState.sortOrder || []).some(
                              (s: any) => s.property === sortProperty
                            )
                              ? '#3B82F6'
                              : 'var(--sort-icon)',
                          }}
                        >
                          {sortProperty ? getSortIcon(sortProperty) : ''}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {header.label !== '' && <span>{header.label}</span>}
                      {renderColumnFilterTrigger(header)}
                      {isSortable(header.moleculeType) && (
                        <button
                          className="sort-icon"
                          aria-label={t('atlas.column.sort', {
                            column:
                              typeof header.label === 'string'
                                ? header.label
                                : t(`context.viewEditor.atlas.molecule.${header.moleculeType}`, {
                                    defaultValue: header.moleculeType,
                                  }),
                          })}
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sortProperty) {
                              toggleSort(sortProperty);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (sortProperty) {
                                toggleSort(sortProperty);
                              }
                            }
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            cursor: 'pointer',
                            marginTop: '3px',
                            color: (filterSortState.sortOrder || []).some(
                              (s: any) => s.property === sortProperty
                            )
                              ? '#3B82F6'
                              : 'var(--sort-icon)',
                          }}
                        >
                          {sortProperty ? getSortIcon(sortProperty) : ''}
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AtlasHeader;
